import { Link } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight, Bluetooth } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import { checkoutAPI } from '../services/api';
import { toast } from 'sonner';
import { useSEO } from '../hooks/useSEO';

const CartPage = () => {
  useSEO({
    title: 'Shopping Cart',
    description: 'Review the BlueSpark tuning modules in your cart before checkout.',
    canonical: '/cart',
    noindex: true,
  });
  const { cartItems, removeFromCart, updateQuantity, clearCart, getCartTotal } = useCart();
  const [loading, setLoading] = useState(false);
  const [addBluetooth, setAddBluetooth] = useState(false);
  const [bluetoothAddon, setBluetoothAddon] = useState({
    id: 'addon-bluetooth',
    name: 'Bluetooth Connectivity Add-on',
    description: 'Wireless app control for power, torque & throttle response',
    price: 99,
  });

  // Fetch authoritative add-on price from backend (so we never mis-display it)
  useEffect(() => {
    checkoutAPI.getAddons()
      .then((d) => { if (d?.bluetooth) setBluetoothAddon(d.bluetooth); })
      .catch(() => { /* keep default */ });
  }, []);

  const subtotal = getCartTotal();
  const addonPrice = addBluetooth ? Number(bluetoothAddon.price || 0) : 0;
  const grandTotal = subtotal + addonPrice;

  const handleCheckout = async () => {
    if (cartItems.length === 0) return;
    setLoading(true);
    try {
      const items = cartItems.map((it) => ({ productId: it.id, quantity: it.quantity }));
      const session = await checkoutAPI.createSession(items, 'aud', { addBluetooth });
      if (!session?.url) throw new Error('No checkout URL returned');
      window.location.href = session.url;
    } catch (error) {
      console.error('Checkout error:', error);
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Unable to start checkout. Please try again.');
      setLoading(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto px-4">
            <ShoppingBag className="h-24 w-24 mx-auto text-gray-300 mb-6" />
            <h1 className="text-3xl font-bold mb-4">Your Cart is Empty</h1>
            <p className="text-gray-600 mb-6">
              Browse our range of performance tuning modules and add items to your cart.
            </p>
            <Link to="/products">
              <Button size="lg">
                Browse Products
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <div className="flex-1">
        <div className="bg-gray-100 py-8">
          <div className="container mx-auto px-4">
            <h1 className="text-3xl md:text-4xl font-bold">Shopping Cart</h1>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold">Cart Items ({cartItems.length})</h2>
                    <Button variant="ghost" size="sm" onClick={clearCart}>
                      Clear Cart
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {cartItems.map((item) => (
                    <div key={item.id} className="flex items-center space-x-4 border-b pb-4">
                      {/* Product Image */}
                      <div className="w-24 h-24 bg-gray-50 rounded flex-shrink-0">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-contain p-2"
                        />
                      </div>

                      {/* Product Details */}
                      <div className="flex-1 min-w-0">
                        <Link to={`/product/${item.id}`}>
                          <h3 className="font-semibold hover:text-blue-600 line-clamp-2">
                            {item.name}
                          </h3>
                        </Link>
                        <p className="text-sm text-gray-600 mt-1">
                          {item.make} {item.model} - {item.engine}
                        </p>
                        <p className="text-lg font-bold text-blue-600 mt-2">
                          ${item.price.toFixed(2)} AUD
                        </p>
                      </div>

                      {/* Quantity Controls */}
                      <div className="flex flex-col items-center space-y-2">
                        <div className="flex items-center border rounded-lg">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="px-4 font-semibold">{item.quantity}</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => removeFromCart(item.id)}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Remove
                        </Button>
                      </div>

                      {/* Item Total */}
                      <div className="hidden md:block text-right">
                        <p className="text-sm text-gray-600">Subtotal</p>
                        <p className="text-xl font-bold">
                          ${(item.price * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Continue Shopping */}
              <Link to="/products" className="inline-block mt-4">
                <Button variant="outline">
                  Continue Shopping
                </Button>
              </Link>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <Card className="sticky top-24">
                <CardHeader>
                  <h2 className="text-xl font-bold">Order Summary</h2>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-semibold" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
                  </div>

                  {/* Bluetooth optional add-on */}
                  <div
                    className={`border rounded-lg p-3 transition-colors ${addBluetooth ? 'border-blue-500 bg-blue-50/60' : 'border-gray-200 bg-gray-50'}`}
                    data-testid="bluetooth-addon-card"
                  >
                    <label className="flex items-start gap-3 cursor-pointer">
                      <Checkbox
                        checked={addBluetooth}
                        onCheckedChange={(v) => setAddBluetooth(v === true)}
                        className="mt-0.5"
                        data-testid="bluetooth-addon-checkbox"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold text-sm flex items-center">
                            <Bluetooth className="h-4 w-4 mr-1.5 text-blue-600" />
                            {bluetoothAddon.name}
                          </span>
                          <span className="text-sm font-bold text-blue-600 whitespace-nowrap">
                            +${Number(bluetoothAddon.price).toFixed(2)}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">
                          {bluetoothAddon.description} (optional add-on)
                        </p>
                      </div>
                    </label>
                  </div>

                  {addBluetooth && (
                    <div className="flex justify-between text-sm" data-testid="cart-addon-line">
                      <span className="text-gray-600">Bluetooth add-on</span>
                      <span className="font-semibold">${addonPrice.toFixed(2)}</span>
                    </div>
                  )}

                  <div className="flex justify-between">
                    <span className="text-gray-600">Shipping</span>
                    <span className="font-semibold text-green-600">Calculated at checkout</span>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex justify-between text-lg">
                      <span className="font-bold">Total</span>
                      <span className="font-bold text-blue-600" data-testid="cart-grand-total">${grandTotal.toFixed(2)} AUD</span>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="flex flex-col space-y-3">
                  <Button size="lg" className="w-full bg-blue-600 hover:bg-blue-700" onClick={handleCheckout} disabled={loading} data-testid="checkout-btn">
                    {loading ? 'Redirecting to Checkout…' : (
                      <span className="flex items-center justify-center">
                        Secure Checkout <ArrowRight className="ml-2 h-5 w-5" />
                      </span>
                    )}
                  </Button>
                  <p className="text-xs text-center text-gray-600">
                    You&apos;ll be redirected to Stripe for secure payment. Card, Apple Pay, Google Pay accepted.
                  </p>
                  <p className="text-xs text-center text-gray-500" data-testid="checkout-terms-notice">
                    By placing your order you agree to our{' '}
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                      data-testid="checkout-terms-link"
                    >
                      Terms of Service
                    </a>
                    .
                  </p>
                </CardFooter>
              </Card>

              {/* Info Card */}
              <Card className="mt-4">
                <CardContent className="p-4">
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">✓</span>
                      Shipping Australia-wide
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">✓</span>
                      2-year warranty included
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">✓</span>
                      30-day money back guarantee
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">✓</span>
                      Secure payment processing
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CartPage;
